import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, DownloadSimple, CaretRight, PencilSimple, Eye } from '@phosphor-icons/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

export default function ArtifactsPanel({ artifact, onClose }) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(artifact?.code || '');

  useEffect(() => { setCode(artifact?.code || ''); setEditing(false); }, [artifact]);

  if (!artifact) return null;

  const handleCopy = () => {
    try { navigator.clipboard.writeText(code); } catch { /* */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const ext = artifact.language === 'python' ? 'py' : artifact.language === 'javascript' ? 'js' : artifact.language === 'html' ? 'html' : artifact.language === 'css' ? 'css' : 'txt';
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lindey-code.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: '50%', opacity: 1 }}
        exit={{ width: 0, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="h-full border-l border-white/10 bg-[#0a0a0a] flex flex-col overflow-hidden"
        data-testid="artifacts-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-12 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-2">
            <CaretRight weight="bold" className="w-3 h-3 text-white/70" />
            <span className="text-[11px] font-mono text-white/50 uppercase tracking-wide">{artifact.language}</span>
            {artifact.title && <span className="text-[11px] text-white/70 ml-1">— {artifact.title}</span>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setEditing(e => !e)} className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${editing ? 'text-lime-400 bg-lime-400/10' : 'text-white/50 hover:text-white/80 hover:bg-gray-800'}`} data-testid="artifact-edit-button">
              {editing ? <Eye className="w-3.5 h-3.5" /> : <PencilSimple className="w-3.5 h-3.5" />}
            </button>
            <button onClick={handleCopy} className="w-7 h-7 rounded-md flex items-center justify-center text-white/50 hover:text-white/60 hover:bg-gray-800 transition-all" data-testid="artifact-copy-button">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
            <button onClick={handleDownload} className="w-7 h-7 rounded-md flex items-center justify-center text-white/50 hover:text-white/60 hover:bg-gray-800 transition-all" data-testid="artifact-download-button">
              <DownloadSimple className="w-3.5 h-3.5" />
            </button>
            <button onClick={onClose} className="w-7 h-7 rounded-md flex items-center justify-center text-white/50 hover:text-white/60 hover:bg-gray-800 transition-all" data-testid="artifact-close-button">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Code */}
        <div className="flex-1 overflow-auto">
          {editing ? (
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              spellCheck={false}
              data-testid="artifact-code-editor"
              className="w-full h-full min-h-full bg-[#0a0a0a] text-[13px] text-gray-200 font-mono p-4 outline-none resize-none leading-[1.7]"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            />
          ) : (
            <SyntaxHighlighter
              language={artifact.language || 'text'}
              style={oneDark}
              showLineNumbers
              customStyle={{
                margin: 0,
                padding: '1rem',
                fontSize: '13px',
                background: '#0a0a0a',
                border: 'none',
                lineHeight: '1.7',
                minHeight: '100%',
              }}
              lineNumberStyle={{ color: '#444', fontSize: '11px', paddingRight: '1rem' }}
            >
              {code}
            </SyntaxHighlighter>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
